import { Container, Graphics, Sprite } from 'pixi.js';
import { campColor, campHot, PALETTE } from './palette';
import {
  drainStrength,
  HIT_FLASHES,
  hitActive,
  hitShake,
  hitStrength,
  impactStrength,
  litCount,
  pipOn,
  TOTEM_BONUS_SOCKETS,
  TOTEM_CRYSTAL,
  TOTEM_FIT,
  TOTEM_SOCKETS,
} from './totem';
import { peekTotemArt, type TotemCamp } from './totemArt';

/** Crest wells first, then the two 1Up lights. */
const SOCKETS = [...TOTEM_SOCKETS, ...TOTEM_BONUS_SOCKETS];
/** A life won back pops in over this long. */
const GAIN_MS = 450;

/** Raster crest: metallic housing, crystal in the sprite, live HP orbs, cheap hit FX. */
export class TotemRig {
  readonly view = new Container();
  private readonly shadow = new Graphics();
  private readonly body = new Sprite();
  private readonly pipGlow = new Graphics();
  private readonly orbs = SOCKETS.map(() => new Sprite());
  private readonly fx = new Graphics();
  private readonly camp: TotemCamp;
  private cell = 32;
  private x0 = 0;
  private y0 = 0;
  private toward: 1 | -1 = 1;
  private hp = 3;
  private ready = false;
  private hitAt = -1;
  private drainAt = -1;
  private drainIndex = -1;
  private gainAt = -1;
  private gainIndex = -1;
  private layoutKey = '';
  private bodyW = 0;
  private bodyH = 0;
  private bodyScale = 1;
  private restX = 0;
  private restY = 0;
  private readonly flashes: number;

  constructor(camp: TotemCamp) {
    this.camp = camp;
    this.flashes = prefersReducedMotion() ? 1 : HIT_FLASHES;
    this.view.eventMode = 'none';
    this.shadow.eventMode = 'none';
    this.body.eventMode = 'none';
    this.pipGlow.eventMode = 'none';
    this.fx.eventMode = 'none';
    this.body.anchor.set(0.5, 0.5);
    this.body.roundPixels = false;
    for (const orb of this.orbs) {
      orb.eventMode = 'none';
      orb.anchor.set(0.5, 0.5);
      orb.roundPixels = false;
    }
    this.view.addChild(this.shadow, this.body, this.pipGlow, ...this.orbs, this.fx);
  }

  place(x0: number, y0: number, cell: number, toward: 1 | -1): void {
    this.x0 = x0;
    this.y0 = y0;
    this.cell = cell;
    this.toward = toward;
    this.layoutKey = '';
  }

  sync(hp: number, now: number): void {
    const next = litCount(hp);
    if (this.ready && next < this.hp) {
      this.hitAt = now;
      this.drainIndex = next;
      this.drainAt = now;
    }
    if (this.ready && next > this.hp) {
      this.gainIndex = next - 1;
      this.gainAt = now;
    }
    this.ready = true;
    this.hp = next;
    this.ensureLayout();
    this.paintOrbs(now);
    this.paintFx(now);
  }

  private ensureLayout(): void {
    const art = peekTotemArt(this.camp);
    if (!art || art.body.width < 2) return;
    const key = `${this.cell}:${this.x0}:${this.y0}:${this.toward}:${art.body.uid}`;
    if (key === this.layoutKey) return;
    this.layoutKey = key;

    this.body.texture = art.body;
    const scale = Math.min(
      (this.cell * TOTEM_FIT.maxW) / art.body.width,
      (this.cell * TOTEM_FIT.maxH) / art.body.height,
    );
    this.body.scale.set(scale);
    this.bodyScale = scale;
    this.bodyW = art.body.width * scale;
    this.bodyH = art.body.height * scale;
    // Sit the crest on the gold rail so the housing cradles the wall.
    this.restX = this.x0 + this.cell;
    this.restY = this.y0 + this.cell * 0.5 - this.toward * this.cell * 0.52;
    this.view.position.set(this.restX, this.restY);

    this.shadow.clear();
    this.shadow.ellipse(0, this.bodyH * 0.28, this.bodyW * 0.42, this.bodyH * 0.14);
    this.shadow.fill({ color: PALETTE.shadow, alpha: 0.4 });

    for (let i = 0; i < SOCKETS.length; i++) {
      const sock = SOCKETS[i]!;
      const orb = this.orbs[i]!;
      orb.position.set((sock.u - 0.5) * this.bodyW, (sock.v - 0.5) * this.bodyH);
    }
  }

  private paintOrbs(now: number): void {
    const art = peekTotemArt(this.camp);
    if (!art) return;
    const destroyed = this.hp <= 0;
    this.body.alpha = destroyed ? 0.42 : 1;
    this.body.tint = destroyed ? 0x6a7078 : 0xffffff;
    const elapsed = now - this.drainAt;
    const drain = drainStrength(elapsed, this.flashes);
    const flashing = hitActive(elapsed, this.flashes);
    const glow = this.pipGlow;
    glow.clear();
    const hot = campHot(this.camp);
    const gain = Math.max(0, 1 - (now - this.gainAt) / GAIN_MS);
    for (let i = 0; i < SOCKETS.length; i++) {
      const sock = SOCKETS[i]!;
      const orb = this.orbs[i]!;
      const bonus = i >= TOTEM_SOCKETS.length;
      const lit = pipOn(this.hp, i);
      const draining = !lit && i === this.drainIndex && flashing;
      const gaining = lit && i === this.gainIndex && gain > 0;
      const size = sock.s * this.bodyW;
      const pop = draining ? 1 + 0.5 * drain : gaining ? 1 + 0.8 * gain : 1;
      orb.width = size * pop;
      orb.height = size * pop;
      // 1Up lights exist only while earned; the crest wells always show.
      orb.visible = !bonus || lit || (draining && drain > 0);
      if (lit || (draining && drain > 0)) {
        orb.texture = art.orbOn;
        orb.alpha = draining ? 0.45 + 0.55 * drain : 1;
        if (bonus) {
          glow.circle(orb.x, orb.y, size * pop * 0.72);
          glow.stroke({ color: PALETTE.gold, width: Math.max(1.5, size * 0.16), alpha: orb.alpha });
        }
        glow.circle(orb.x, orb.y, size * pop);
        glow.fill({ color: hot, alpha: (draining ? 0.7 : 0.28) * orb.alpha });
        if (gaining) {
          glow.circle(orb.x, orb.y, size * (1 + 1.6 * (1 - gain)));
          glow.stroke({ color: PALETTE.goldHot, width: 2.5, alpha: gain });
        }
      } else {
        orb.texture = art.orbOff;
        orb.alpha = destroyed ? 0.35 : 0.85;
      }
    }
  }

  private paintFx(now: number): void {
    const g = this.fx;
    g.clear();
    const elapsed = now - this.hitAt;
    const hit = hitStrength(elapsed, this.flashes);
    const boom = impactStrength(elapsed, this.flashes);
    const shake = hitShake(elapsed, this.flashes);
    this.view.position.set(this.restX + shake.x * this.cell, this.restY + shake.y * this.cell);
    this.view.rotation = shake.rot;
    const destroyed = this.hp <= 0;
    const dim = destroyed ? 0.42 : 1;
    this.body.alpha = dim + (1 - dim) * hit;
    this.body.scale.set(this.bodyScale * (1 + 0.07 * hit));
    if (hit > 0) this.body.tint = 0xffffff;
    const cx = (TOTEM_CRYSTAL.u - 0.5) * this.bodyW;
    const cy = (TOTEM_CRYSTAL.v - 0.5) * this.bodyH;
    const rx = TOTEM_CRYSTAL.su * this.bodyW * 0.5;
    const ry = TOTEM_CRYSTAL.sv * this.bodyH * 0.5;
    if (!destroyed) {
      const pulse = this.hp <= 1 ? 0.16 + 0.14 * Math.abs(Math.sin(now * 0.008)) : 0.12;
      g.ellipse(cx, cy, rx * 1.35, ry * 1.28);
      g.fill({ color: campColor(this.camp), alpha: 0.2 + pulse });
    }
    if (hit <= 0 && boom <= 0) return;
    if (hit > 0) {
      g.ellipse(0, 0, this.bodyW * 0.5, this.bodyH * 0.48);
      g.fill({ color: campHot(this.camp), alpha: 0.62 * hit });
      g.ellipse(0, 0, this.bodyW * 0.36, this.bodyH * 0.34);
      g.fill({ color: PALETTE.white, alpha: 0.78 * hit });
      g.ellipse(cx, cy, rx * 1.15, ry * 1.1);
      g.fill({ color: PALETTE.white, alpha: 0.9 * hit });
    }
    if (boom > 0) {
      const r = Math.max(rx, ry, this.bodyW * 0.22);
      g.circle(cx, cy, r * (0.85 + (1 - boom) * 1.35));
      g.stroke({ color: PALETTE.white, width: 3.4, alpha: 0.95 * boom });
      g.circle(cx, cy, r * (0.5 + (1 - boom) * 0.9));
      g.stroke({ color: campHot(this.camp), width: 2.6, alpha: 0.9 * boom });
    }
  }
}

function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}
